// ABOUTME: Shared transform functions for lead time API responses (Order Intake & Order Cancellation).
// ABOUTME: Maps calendar UoM to business UoM and converts API response to form-ready config shape.

import {DAYS, DayIdentifier} from '../types/deliveryWindow'
import {
  LeadTimeRuleResponse,
  NonStandardLeadTimeRuleResponse,
  OrderIntakeLeadTimeApiResponse,
  OrderIntakeLeadTimeConfig,
  OrderIntakeLeadTimeFormValues,
  StandardLeadTimeFormValues,
  NonStandardLeadTimeFormValues,
  toDayIdentifier,
} from '../types/orderIntakeLeadTime'

/** Maps calendar UoM IDs to their business-time equivalents.
 * TODO: Remove once backend seed data uses business UoM IDs.
 * The global config seed data uses calendar units (min, h, d) but
 * the domain expects business-time units. This mapping bridges the gap
 * until the backend seed data is updated. */
const CALENDAR_TO_BUSINESS_UOM: Record<string, string> = {
  min: 'businessMinute',
  h: 'businessHour',
  d: 'businessDay',
}

const BUSINESS_UOM_DISPLAY: Record<string, string> = {
  businessMinute: 'business mins',
  businessHour: 'business hours',
  businessDay: 'business days',
}

const toBusinessUomId = (id: string): string => CALENDAR_TO_BUSINESS_UOM[id] ?? id
const toBusinessUomDisplay = (id: string, fallback: string): string => BUSINESS_UOM_DISPLAY[id] ?? fallback

export const transformStandard = (rule: LeadTimeRuleResponse): StandardLeadTimeFormValues => {
  const baseUomId = toBusinessUomId(rule.baseLeadTimeUom.id)
  const modUomId = toBusinessUomId(rule.farDistanceModifierUom.id)
  return {
    applicableDays: rule.applicableDays,
    baseLeadTime: rule.baseLeadTime,
    baseLeadTimeUomId: baseUomId,
    baseLeadTimeUomDisplay: toBusinessUomDisplay(baseUomId, rule.baseLeadTimeUom.displaySymbol),
    referenceEvent: rule.referenceEvent,
    farDistanceModifier: rule.farDistanceModifier,
    farDistanceModifierUomId: modUomId,
    farDistanceModifierUomDisplay: toBusinessUomDisplay(modUomId, rule.farDistanceModifierUom.displaySymbol),
    calculationMethod: rule.calculationMethod,
  }
}

export const transformNonStandard = (rule: NonStandardLeadTimeRuleResponse): NonStandardLeadTimeFormValues => {
  const baseUomId = toBusinessUomId(rule.baseLeadTimeUom.id)
  const modUomId = toBusinessUomId(rule.farDistanceModifierUom.id)
  return {
    day: rule.day,
    baseLeadTime: rule.baseLeadTime,
    baseLeadTimeUomId: baseUomId,
    baseLeadTimeUomDisplay: toBusinessUomDisplay(baseUomId, rule.baseLeadTimeUom.displaySymbol),
    referenceEvent: rule.referenceEvent,
    farDistanceModifier: rule.farDistanceModifier,
    farDistanceModifierUomId: modUomId,
    farDistanceModifierUomDisplay: toBusinessUomDisplay(modUomId, rule.farDistanceModifierUom.displaySymbol),
    calculationMethod: rule.calculationMethod,
  }
}

export const transformLeadTimeResponse = (response: OrderIntakeLeadTimeApiResponse, parentValues?: OrderIntakeLeadTimeFormValues): OrderIntakeLeadTimeConfig => {
  const leadTimesValue = response.leadTimes?.value
  const thresholdValue = response.farDistanceThreshold?.value
  const ownFormValues: OrderIntakeLeadTimeFormValues = {
    farDistanceThreshold: thresholdValue?.threshold ?? 0,
    farDistanceThresholdUomId: thresholdValue?.uom.id ?? '',
    farDistanceThresholdUomDisplay: thresholdValue?.uom.displaySymbol ?? '',
    standard: leadTimesValue?.standard ? transformStandard(leadTimesValue.standard) : null,
    nonStandard: [...(leadTimesValue?.nonStandard?.map(transformNonStandard) ?? [])].sort((a, b) => {
      const aIdx = DAYS.indexOf(toDayIdentifier(a.day) as DayIdentifier)
      const bIdx = DAYS.indexOf(toDayIdentifier(b.day) as DayIdentifier)
      return (aIdx === -1 ? DAYS.length : aIdx) - (bIdx === -1 ? DAYS.length : bIdx)
    }),
  }
  return {
    id: response.id,
    ...ownFormValues,
    distanceUomOptions: [{
      value: thresholdValue?.uom?.id ?? 'km',
      label: thresholdValue?.uom?.displaySymbol ?? 'km',
    }],
    overrides: {
      farDistanceThreshold: response.farDistanceThreshold?.isOverridden ?? false,
      leadTimes: response.leadTimes?.isOverridden ?? false,
    },
    parentValues: parentValues ?? ownFormValues,
    _meta: {
      scopeId: response.scopeId,
      isInherited: !(
        (response.farDistanceThreshold?.isOverridden ?? false) ||
        (response.leadTimes?.isOverridden ?? false)
      ),
      resolvedFrom: null,
      createdAt: response._meta.createdAt,
      updatedAt: response._meta.updatedAt,
      createdBy: response._meta.createdBy,
      updatedBy: response._meta.updatedBy,
    },
  }
}
